/**
 * Setting payment API — ช่วงยอดฝาก, จับคู่ธนาคาร, filter status, กฎช่องทางฝาก (payment rules)
 */
import axios from '@/axios'

const BASE = '/setting-payment'

/**
 * GET - ดึงรายการขั้นทั้งหมด
 * @returns {Promise<Array>}
 */
export async function getRanges () {
  const res = await axios.get(BASE)
  return res.data
}

/**
 * GET - ดึงรายการ bank_lists สำหรับ dropdown
 * @param {{ type?: string }} params - optional, e.g. { type: 'payment' }
 * @returns {Promise<Array>}
 */
export async function getBankLists (params = {}) {
  const res = await axios.get(`${BASE}/bank-lists`, { params })
  return res.data
}

/**
 * POST - อัปเดตขั้นต่ำฝาก (min_deposit) ใน bank_lists
 * @param {string} code - bank_lists.code
 * @param {number|string} min_deposit
 * @returns {Promise<any>}
 */
export async function updatePaymentMinDeposit (code, min_deposit) {
  const res = await axios.post(`${BASE}/update-min-deposit`, {
    code: String(code),
    min_deposit
  })
  return res.data
}

/**
 * POST - เพิ่มขั้นเดียว
 * @param {{ min_amount: number, payment_code: string, is_active?: boolean }} data
 * @returns {Promise<{ status: boolean, data: Array }>}
 */
export async function createRange (data) {
  const res = await axios.post(BASE, data)
  return res.data
}

/**
 * POST - เพิ่มหลายขั้น
 * @param {{ ranges: Array<{ min_amount: number, payment_code: string, is_active?: boolean }> }} data
 * @returns {Promise<{ status: boolean, data: Array }>}
 */
export async function createRanges (data) {
  const res = await axios.post(BASE, data)
  return res.data
}

/**
 * PUT - แก้ไขขั้น
 * @param {number} id
 * @param {{ min_amount?: number, payment_code?: string, is_active?: boolean }} data
 * @returns {Promise<{ status: boolean, data: Array }>}
 */
export async function updateRange (id, data) {
  const res = await axios.put(`${BASE}/${id}`, data)
  return res.data
}

/**
 * DELETE - ลบขั้น
 * @param {number} id
 * @returns {Promise<{ status: boolean, data: Array }>}
 */
export async function deleteRange (id) {
  const res = await axios.delete(`${BASE}/${id}`)
  return res.data
}

// --- จับคู่ payment กับธนาคารลูกค้า ---
const MAPPING_PATH = `${BASE}/bank-payment-mapping`

/**
 * GET - ดึงการจับคู่ธนาคารลูกค้า -> payment และ default payment
 * @returns {Promise<{ bankPaymentMapping: Object<string, string>, defaultPaymentCode: string }>}
 */
export async function getBankPaymentMapping () {
  const res = await axios.get(MAPPING_PATH)
  return res.data
}

/**
 * POST - บันทึกการจับคู่และ default payment
 * @param {{ bankPaymentMapping: Object<string, string>, defaultPaymentCode: string }} data
 * @returns {Promise<any>}
 */
export async function saveBankPaymentMapping (data) {
  const res = await axios.post(MAPPING_PATH, data)
  return res.data
}

/**
 * GET - ดึงสถานะ setting ระบบคัดยอดฝาก payment (เปิด = filter ด้วยยอดฝาก, ปิด = filter ด้วยธนาคารลูกค้า)
 * proxy จาก /setting-payment/filter-status
 * @returns {Promise<{ setting_status: boolean }>}
 */
export async function getDepositRangesFilterStatus () {
  const res = await axios.get(`${BASE}/filter-status`)
  const data = res.data || {}
  return {
    setting_status: !!data.setting_status
  }
}

/**
 * PUT - เปิด/ปิด setting ระบบคัดยอดฝาก payment
 * proxy ไปที่ /setting-payment/filter-status
 * @param {boolean} status - true = filter ด้วยยอดฝาก (ON), false = filter ด้วยธนาคารลูกค้า (OFF)
 * @returns {Promise<{ status: boolean, setting_status: boolean }>}
 */
export async function updateDepositRangesFilterStatus (status) {
  const res = await axios.put(`${BASE}/filter-status`, {
    setting_status: status
  })
  return res.data
}

// --- Payment rules (rule engine ช่องทางฝาก) ---
// Response ฝั่ง backend อาจใช้ HTTP 200 ทุกกรณี — เช็ค body.status ตามสัญญา API

/**
 * GET - ดึงรายการกฎช่องทางฝากทั้งหมด
 * @param {{ active_only?: boolean|string }} params
 * @returns {Promise<{ status: boolean, data?: Array, message?: string }>}
 */
export async function getPaymentRules (params = {}) {
  const res = await axios.get(`${BASE}/payment-rules`, { params })
  return res.data
}

/**
 * GET - ดึงกฎช่องทางฝากตาม id
 * @returns {Promise<{ status: boolean, data?: object, message?: string }>}
 */
export async function getPaymentRule (id) {
  const res = await axios.get(`${BASE}/payment-rules/${id}`)
  return res.data
}

/**
 * POST /setting-payment/payment-rules
 * @param {object} data - เช่น payment_code, conditions, is_active, priority, name (optional), description (สรุปเงื่อนไขอัตโนมัติจาก UI)
 * @returns {Promise<{ status: boolean, data?: object, message?: string }>}
 */
export async function createPaymentRule (data) {
  const res = await axios.post(`${BASE}/payment-rules`, data)
  return res.data
}

/**
 * POST /setting-payment/payment-rules/:id
 * @param {object} data - เช่น name, conditions, is_active, payment_code, description (สรุปเงื่อนไขอัตโนมัติจาก UI)
 * @returns {Promise<{ status: boolean, data?: object, message?: string }>}
 */
export async function updatePaymentRule (id, data) {
  const res = await axios.post(`${BASE}/payment-rules/${id}`, data)
  return res.data
}

/**
 * DELETE /setting-payment/payment-rules/:id — ลบกฎถาวร (API เก็บแถวเดิมไว้ใน log_usage)
 * @returns {Promise<{ status: boolean, data?: object, message?: string }>}
 */
export async function deletePaymentRule (id) {
  const res = await axios.delete(`${BASE}/payment-rules/${id}`)
  return res.data
}

/**
 * GET - ดึงรายการ context keys สำหรับกฎ (เช่น ใช้ใน builder)
 * @param {{ active_only?: boolean|string }} params
 * @returns {Promise<{ status?: boolean, data?: Array } | Array>}
 */
export async function getPaymentRuleContextKeys (params = {}) {
  const res = await axios.get(`${BASE}/payment-rule-context-keys`, { params })
  return res.data
}

/**
 * POST /setting-payment/payment-rules/preview
 * จำลอง engine เดียวกับหน้า member — ส่ง username + deposit_amount หรือ context เต็ม
 * @param {Record<string, unknown>} body
 * @returns {Promise<{ status: boolean, context?: object, payment_code?: string | null, matched_rule?: object | null, message?: string }>}
 */
export async function previewPaymentRules (body) {
  const res = await axios.post(`${BASE}/payment-rules/preview`, body)
  return res.data
}

/**
 * GET /setting-payment/payment-auto-failover — สถานะ Payment Auto Failover (ไม่มีแถว = ปิด)
 * @returns {Promise<{ status: boolean, setting_status: boolean }>}
 */
export async function getPaymentAutoFailoverStatus () {
  const res = await axios.get(`${BASE}/payment-auto-failover`)
  return res.data
}

/**
 * POST /setting-payment/payment-auto-failover — เปิด/ปิด Payment Auto Failover
 * @param {boolean} status
 * @returns {Promise<{ status: boolean, setting_status?: boolean, message?: string }>}
 */
export async function updatePaymentAutoFailoverStatus (status) {
  const res = await axios.post(`${BASE}/payment-auto-failover`, { setting_status: status })
  return res.data
}
